import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    const dialog = document.getElementById('quick-add-dialog');
    if (!(dialog instanceof QuickAddDialog)) return;
    const token = Symbol('quick-add');
    dialog.requestToken = token;
    dialog.trigger = this.querySelector('button');
    dialog.dataset.intent = this.dataset.sizeIntent === 'true' ? 'required' : '';
    const touch = event.pointerType === 'touch' || event.pointerType === 'pen';
    dialog.dataset.presentation = window.innerWidth < 750 || touch || (!event.pointerType && !matchMedia('(hover:hover) and (pointer:fine)').matches) ? 'sheet' : 'chooser';
    const content = document.getElementById('quick-add-modal-content');
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = dialog.dataset.loading;
    content.replaceChildren(status);
    this.#openQuickAddModal();
    try {
      let productGrid = this.#cachedContent.get(currentUrl);
      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        productGrid = html?.querySelector('[data-product-grid-content]');
        if (!productGrid) throw new Error('Missing product content');
        this.#cachedContent.set(currentUrl, productGrid.cloneNode(true));
      }
      if (dialog.requestToken !== token) return;
      await this.updateQuickAddModal(productGrid.cloneNode(true));
      dialog.resetIntent();
    } catch (error) {
      if (dialog.requestToken !== token) return;
      status.textContent = dialog.dataset.loadError;
      status.setAttribute('role', 'alert');
      const retry = document.createElement('button');
      retry.type = 'button'; retry.textContent = dialog.dataset.retry;
      retry.addEventListener('click', (retryEvent) => this.handleClick(retryEvent), { once: true });
      const link = document.createElement('a');
      link.href = currentUrl; link.textContent = dialog.dataset.viewProduct;
      content.replaceChildren(status, retry, link);
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (document.getElementById('quick-add-dialog')?.dataset.intent === 'required') {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      productFormComponent?.setAttribute('on:submit', 'quick-add-dialog/submitSelectedSize');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        this.#formatRupeePrice(productPrice);
        productHeader.appendChild(productPrice);
      }
      if (this.dataset.productColour) {
        const colourLabel = document.createElement('p');
        colourLabel.textContent = this.dataset.productColour;
        productHeader.appendChild(colourLabel);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        const legend = variantPicker.querySelector('legend');
        if (legend) legend.textContent = 'SELECT SIZE';
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        if (!productFormComponent.querySelector('[data-caddy-buy-now]')) {
          const buyNow = document.createElement('button');
          buyNow.type = 'button';
          buyNow.className = 'caddy-buy-now';
          buyNow.dataset.caddyBuyNow = '';
          buyNow.textContent = 'BUY NOW →';
          productFormComponent.querySelector('.product-form-buttons')?.append(buyNow);
        }
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
      productGrid.querySelectorAll('.product-information__media, [data-caddy-size-guide-open], [data-caddy-size-error], .shopify-payment-button, quantity-selector-component').forEach((node) => node.remove());
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    if (document.getElementById('quick-add-dialog')?.dataset.intent !== 'required') this.#syncVariantSelection(modalContent);
  }

  /** @param {Element} element */
  #formatRupeePrice(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      node.textContent = node.textContent.replace(/Rs\.\s*([\d,]+)\.00/g, '₹$1');
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  requestToken;
  trigger;
  #intent = false;
  #submitting = false;
  #buyNowRequested = false;

  #ensureBuyNow() {
    const productForm = this.querySelector('product-form-component');
    if (!productForm || productForm.querySelector('[data-caddy-buy-now]')) return;
    const buyNow = document.createElement('button');
    buyNow.type = 'button';
    buyNow.className = 'caddy-buy-now';
    buyNow.dataset.caddyBuyNow = '';
    buyNow.textContent = 'BUY NOW →';
    productForm.querySelector('.product-form-buttons')?.append(buyNow);
  }

  /** @param {'select'|'ready'|'adding'|'added'} state */
  #setPurchaseState(state) {
    this.#ensureBuyNow();
    this.dataset.purchaseState = state;
    this.dataset.sizeSelected = String(state !== 'select');
    const button = this.querySelector('[ref="addToCartButton"]');
    if (button) {
      const labels = { select: 'SELECT A SIZE', ready: 'ADD TO BAG', adding: 'ADDING…', added: 'ADDED ✓' };
      const textNode = [...button.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (textNode) textNode.textContent = labels[state];
      else button.prepend(document.createTextNode(labels[state]));
      button.setAttribute('aria-label', labels[state]);
    }
    const buyNow = this.querySelector('[data-caddy-buy-now]');
    if (buyNow) {
      buyNow.disabled = state !== 'ready';
      buyNow.setAttribute('aria-disabled', String(state !== 'ready'));
      buyNow.textContent = 'BUY NOW →';
    }
  }

  resetIntent() {
    this.#intent = false;
    this.#submitting = false;
    this.#buyNowRequested = false;
    if (this.dataset.intent !== 'required') return;
    this.querySelectorAll('variant-picker input[type="radio"]').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
      input.disabled = input.dataset.optionAvailable === 'false';
    });
    this.querySelectorAll('input[name="id"]').forEach((input) => { input.value = ''; });
    this.querySelectorAll('[ref="addToCartButton"]').forEach((button) => { button.disabled = false; button.setAttribute('aria-disabled', 'true'); });
    this.#setPurchaseState('select');
    this.#markUnavailable();
  }

  #markUnavailable() {
    this.querySelectorAll('variant-picker input[data-option-available="false"]').forEach((input) => {
      input.disabled = true;
      const label = input.closest('label');
      if (label && !label.querySelector('[data-unavailable-label]')) {
        const text = document.createElement('span');
        text.dataset.unavailableLabel = ''; text.textContent = this.dataset.unavailable;
        label.append(text);
      }
    });
  }

  #intentChange = (event) => {
    if (this.dataset.intent !== 'required' || !event.target.matches('variant-picker input[type="radio"]')) return;
    this.#intent = event.target.checked && !event.target.disabled && event.target.dataset.optionAvailable !== 'false';
    this.querySelector('[data-quick-size-error]')?.remove();
    this.querySelectorAll('[ref="addToCartButton"]').forEach((button) => button.setAttribute('aria-disabled', String(!this.#intent)));
    this.#setPurchaseState(this.#intent ? 'ready' : 'select');
  };

  // Horizon delegates on:submit at document capture. Route that handler here
  // before invoking the existing product form, rather than guarding too late.
  submitSelectedSize = (event) => {
    if (!this.#guardSubmit(event)) return;
    const form = event.target.closest('product-form-component');
    form?.handleSubmit(event);
  };

  #guardSubmit = (event) => {
    if (this.dataset.intent !== 'required') return true;
    const selected = this.querySelector('variant-picker input[type="radio"]:checked');
    if (this.#submitting || !this.#intent || !selected || selected.disabled || selected.dataset.optionAvailable === 'false') {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.#submitting) return false;
      let error = this.querySelector('[data-quick-size-error]');
      if (!error) {
        error = document.createElement('p'); error.dataset.quickSizeError = ''; error.setAttribute('role', 'alert');
        this.querySelector('variant-picker')?.append(error);
      }
      error.textContent = this.dataset.required;
      this.querySelector('variant-picker input:not(:disabled)')?.focus();
      return false;
    }
    const button = this.querySelector('[ref="addToCartButton"]');
    if (button?.disabled) {
      event.preventDefault(); event.stopImmediatePropagation();
      return false;
    }
    this.#submitting = true;
    this.#setPurchaseState('adding');
    return true;
  };

  #handleBuyNow = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-caddy-buy-now]') : null;
    if (!button || !this.#intent || this.#submitting) return;
    event.preventDefault();
    this.#buyNowRequested = true;
    this.querySelector('product-form-component form')?.requestSubmit();
  };

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.addEventListener('change', this.#intentChange, { capture: true, signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleBuyNow, { capture: true, signal: this.#abortController.signal });


    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        this.#submitting = false;
        if (detail?.didError) {
          if (this.dataset.intent === 'required') this.resetIntent();
          return;
        }
        this.#setPurchaseState('added');
        const buyNowRequested = this.#buyNowRequested;
        window.setTimeout(() => {
          if (buyNowRequested) window.location.assign('/checkout');
          else this.closeDialog();
        }, 1800);
      })
      .catch((error) => {
        this.#submitting = false;
        if (error?.name !== 'AbortError') {
          const message = document.createElement('p'); message.setAttribute('role', 'alert');
          message.textContent = this.dataset.loadError; this.querySelector('#quick-add-modal-content')?.append(message);
        }
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        this.#markUnavailable();
        this.#ensureBuyNow();
        this.#setPurchaseState(this.#intent ? 'ready' : 'select');
        this.querySelectorAll('[data-caddy-size-guide-open], [data-caddy-size-error]').forEach((node) => node.remove());
        const productPrice = this.querySelector('.product-header product-price');
        if (productPrice) {
          const walker = document.createTreeWalker(productPrice, NodeFilter.SHOW_TEXT);
          let node;
          while ((node = walker.nextNode())) node.textContent = node.textContent.replace(/Rs\.\s*([\d,]+)\.00/g, '₹$1');
        }

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    this.requestToken = null;
    this.#submitting = false;
    this.#buyNowRequested = false;
    // Horizon's cart dialog wraps cart-drawer-component; preserve its focus handoff.
    if (!document.querySelector('#cart-drawer > dialog[open]')) this.trigger?.focus({ preventScroll: true });
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
